/**
 * Select Component
 * Pure presentation component for dropdown selection.
 */

import type { ChangeEvent, FocusEvent, ReactNode, SelectHTMLAttributes } from 'react';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'onBlur' | 'onFocus'> {
  children: ReactNode;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: FocusEvent<HTMLSelectElement>) => void;
  onFocus?: (event: FocusEvent<HTMLSelectElement>) => void;
}

export function Select({
  value,
  disabled = false,
  className = '',
  children,
  onChange,
  onBlur,
  onFocus,
  ...restProps
}: SelectProps) {
  const baseClasses = [
    'w-full',
    'px-[var(--space-3)]',
    'py-[var(--space-2)]',
    'bg-[var(--surface-bg-input)]',
    'text-[var(--surface-text-primary)]',
    'border',
    'border-[var(--surface-border-default)]',
    'rounded-[var(--radius-lg)]',
    'focus:outline-none',
    'focus:border-[var(--surface-border-focus)]',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
  ].join(' ');

  const classes = `${baseClasses} ${className}`.trim();

  return (
    <select
      value={value}
      disabled={disabled}
      className={classes}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      {...restProps}
    >
      {children}
    </select>
  );
}
